/**
 * Lokaso analytics — GA4 wrapper.
 *
 * Loaded only when:
 *  - REACT_APP_GA4_ID is configured (e.g. "G-XXXXXXXXXX")
 *  - hostname includes "lokasos" (skips preview/dev to keep stats clean)
 *
 * Public API:
 *   initAnalytics()              → installs the gtag script (idempotent)
 *   trackPageView(path)          → sends a page_view event (SPA route change)
 *   trackEvent(name, params)     → sends a custom GA4 event
 *
 * Custom events used by Lokaso:
 *   - search          { search_term }
 *   - whatsapp_click  { business_slug }
 *   - call_click      { business_slug }
 *   - claim_started   { business_slug }
 *   - share_click     { business_slug, channel }
 *
 * PWA: gtag.js works inside the standalone app shell exactly like in the
 * browser tab — no extra config needed.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA4_ID || "";

const isEnabled = () => {
  if (!MEASUREMENT_ID) return false;
  if (typeof window === "undefined") return false;
  // Track only on the production domain — skip preview & localhost
  const host = window.location.hostname || "";
  return host.includes("lokasos") || host === "lokasos.com";
};

let _initialized = false;

export function initAnalytics() {
  if (_initialized || !isEnabled()) return;
  _initialized = true;

  // Ensure gtag stub + consent defaults exist BEFORE gtag('config', ...).
  // (Defaults are also set by cookieConsent.setConsentDefaults, but call it
  //  again defensively — Google Consent Mode is idempotent.)
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });

  // Inject GA4 loader
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    // SPA: we send page_views manually from a route effect, so disable auto.
    send_page_view: false,
    // Privacy hardening
    anonymize_ip: true,
  });
}

export function trackPageView(path) {
  if (!isEnabled() || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!isEnabled() || typeof window.gtag !== "function") {
    // Dev-only echo so we can see events firing during development
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug(`[analytics] (skipped) ${name}`, params);
    }
    return;
  }
  window.gtag("event", name, params);
}
